import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useTheme } from '../../contexts/ThemeContext';

const AnalyticsDashboard = () => {
  const { isDarkMode } = useTheme();
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalServers: 0,
    totalWorkflows: 0,
    activeUsers: 0,
    browserLoggedInUsers: 0,
    mcpProxyConnectedUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBrowserUsersModal, setShowBrowserUsersModal] = useState(false);
  const [browserLoggedInUsersDetails, setBrowserLoggedInUsersDetails] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const fetchAnalytics = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Fetch analytics from the new endpoint
      const analyticsResponse = await apiClient.get('/monitoring/analytics');
      const analyticsData = analyticsResponse.data.data || {};

      setAnalytics({
        totalUsers: analyticsData.totalUsers || 0,
        totalServers: analyticsData.totalServers || 0,
        totalWorkflows: analyticsData.totalWorkflows || 0,
        activeUsers: analyticsData.activeUsers || 0,
        browserLoggedInUsers: analyticsData.browserLoggedInUsers || 0,
        mcpProxyConnectedUsers: analyticsData.mcpProxyConnectedUsers || 0
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBrowserLoggedInClick = async () => {
    setLoadingUsers(true);
    setShowBrowserUsersModal(true);
    
    try {
      const response = await apiClient.get('/monitoring/analytics/browser-users');
      setBrowserLoggedInUsersDetails(response.data.data.users || []);
    } catch (err) {
      console.error('Error fetching browser logged-in users:', err);
      setError('Failed to fetch user details');
    } finally {
      setLoadingUsers(false);
    }
  };

  const BrowserUsersModal = () => {
    if (!showBrowserUsersModal) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: isDarkMode ? '#2a2a2a' : '#fff',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          border: `1px solid ${isDarkMode ? '#444' : '#e9ecef'}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            borderBottom: `1px solid ${isDarkMode ? '#444' : '#e9ecef'}`,
            paddingBottom: '1rem'
          }}>
            <h3 style={{
              color: isDarkMode ? 'white' : '#333',
              margin: 0,
              fontSize: '1.5rem'
            }}>
              Browser Logged In Users
            </h3>
            <button
              onClick={() => setShowBrowserUsersModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: isDarkMode ? '#ccc' : '#666',
                padding: '0.5rem',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = isDarkMode ? '#444' : '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </div>

          {loadingUsers ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #17a2b8',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
              <p style={{ color: isDarkMode ? '#ccc' : '#666', marginTop: '1rem' }}>
                Loading user details...
              </p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#dc3545' }}>
              {error}
            </div>
          ) : browserLoggedInUsersDetails.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: isDarkMode ? '#ccc' : '#666' }}>
              No users currently logged in via browser
            </div>
          ) : (
            <div>
              <p style={{
                color: isDarkMode ? '#ccc' : '#666',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                Found {browserLoggedInUsersDetails.length} user{browserLoggedInUsersDetails.length !== 1 ? 's' : ''} with active browser sessions
              </p>
              
              {browserLoggedInUsersDetails.map((user) => (
                <div key={user.id} style={{
                  border: `1px solid ${isDarkMode ? '#444' : '#e9ecef'}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  backgroundColor: isDarkMode ? '#333' : '#f8f9fa'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                  }}>
                    <div>
                      <strong style={{ color: isDarkMode ? 'white' : '#333' }}>
                        {user.username}
                      </strong>
                      <br />
                      <span style={{ color: isDarkMode ? '#ccc' : '#666', fontSize: '0.9rem' }}>
                        {user.email}
                      </span>
                    </div>
                    <span style={{
                      color: '#28a745',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      ACTIVE
                    </span>
                  </div>
                  
                  {user.loginHistory && user.loginHistory.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div style={{
                        color: isDarkMode ? '#aaa' : '#555',
                        fontSize: '0.8rem',
                        marginBottom: '0.25rem'
                      }}>
                        Recent logins:
                      </div>
                      {user.loginHistory.slice(0, 3).map((login, index) => (
                        <div key={login.id} style={{
                          fontSize: '0.8rem',
                          color: isDarkMode ? '#bbb' : '#666',
                          marginBottom: '0.25rem'
                        }}>
                          {new Date(login.timestamp).toLocaleString()} 
                          {login.ipAddress && ` • ${login.ipAddress}`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <div
      style={{
        backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
        border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
        borderRadius: '8px',
        padding: '2rem',
        textAlign: 'center',
        boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: onClick ? 'pointer' : 'default',
        flex: 1,
        minWidth: '200px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = isDarkMode 
          ? '0 4px 16px rgba(0,0,0,0.5)' 
          : '0 4px 16px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isDarkMode 
          ? '0 2px 8px rgba(0,0,0,0.3)' 
          : '0 2px 8px rgba(0,0,0,0.1)';
      }}
      onClick={onClick}
    >
      <div style={{ marginBottom: '1rem', fontSize: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {typeof icon === 'string' ? icon : <div style={{ color }}>{icon}</div>}
      </div>
      <div
        style={{
          fontSize: '0.9rem',
          color: isDarkMode ? '#aaa' : '#666',
          marginBottom: '0.5rem',
          textTransform: 'uppercase',
          fontWeight: '500',
          letterSpacing: '0.05em'
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          color: color,
          marginBottom: '0.5rem'
        }}
      >
        {value}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        color: isDarkMode ? '#aaa' : '#666'
      }}>
        Loading analytics...
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 0' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{
          margin: 0,
          marginBottom: '1rem',
          color: isDarkMode ? 'white' : '#333'
        }}>
          System Analytics
        </h2>
        <button
          onClick={fetchAnalytics}
          disabled={loading}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: isDarkMode ? '#0099FF' : '#0099FF',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            fontSize: '0.9rem',
            fontWeight: '600'
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh Now'}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '2rem' }}>
          {error}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}
      >
        <StatCard
          title="Total Users"
          value={analytics.totalUsers}
          icon="👥"
          color={isDarkMode ? 'white' : '#333'}
        />
        <StatCard
          title="Total MCP Servers"
          value={analytics.totalServers}
          icon={
            <svg width="48" height="48" viewBox="0 0 32 32" fill="currentColor" style={{ color: '#0099FF' }}>
              <rect x="6" y="6" width="20" height="6" rx="1" fill="currentColor"/>
              <rect x="6" y="14" width="20" height="6" rx="1" fill="currentColor"/>
              <rect x="6" y="22" width="20" height="6" rx="1" fill="currentColor"/>
            </svg>
          }
          color="#0099FF"
        />
        <StatCard
          title="Total Workflows"
          value={analytics.totalWorkflows}
          icon={
            <svg width="48" height="48" viewBox="0 0 32 32" fill="currentColor" style={{ color: '#00CC66' }}>
              <rect x="12" y="4" width="8" height="8" rx="2" fill="currentColor"/>
              <rect x="4" y="20" width="8" height="8" rx="2" fill="currentColor"/>
              <rect x="20" y="20" width="8" height="8" rx="2" fill="currentColor"/>
              <line x1="16" y1="12" x2="8" y2="20" stroke="currentColor" strokeWidth="2"/>
              <line x1="16" y1="12" x2="24" y2="20" stroke="currentColor" strokeWidth="2"/>
              <line x1="8" y1="24" x2="24" y2="24" stroke="currentColor" strokeWidth="2"/>
            </svg>
          }
          color="#00CC66"
        />
        <StatCard
          title="New Users (7d)"
          value={analytics.activeUsers}
          icon="🔥"
          color="#dc3545"
        />
        <StatCard
          title="Users Logged In"
          value={analytics.browserLoggedInUsers}
          icon="👥"
          color="#17a2b8"
          onClick={handleBrowserLoggedInClick}
        />
        <StatCard
          title="MCP-Proxy Connected"
          value={analytics.mcpProxyConnectedUsers}
          icon="🔗"
          color="#6f42c1"
        />
      </div>

      {/* Additional Info Card */}
      <div
        style={{
          backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
          border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
          borderRadius: '8px',
          padding: '2rem',
          marginTop: '2rem'
        }}
      >
        <h3 style={{
          color: isDarkMode ? 'white' : '#333',
          marginBottom: '1rem',
          fontSize: '1.2rem'
        }}>
          System Information
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          color: isDarkMode ? '#ccc' : '#555'
        }}>
          <div>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>
              System Status
            </div>
            <div style={{ fontSize: '1.1rem', color: '#28a745' }}>
              ● Operational
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>
              Last Updated
            </div>
            <div style={{ fontSize: '1rem' }}>
              {new Date().toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>
              API Version
            </div>
            <div style={{ fontSize: '1rem' }}>
              v1.0
            </div>
          </div>
        </div>
      </div>
      <BrowserUsersModal />
    </div>
  );
};

export default AnalyticsDashboard;

